/**
 * Date utility functions for handling API/display date formats.
 * - API: YYYY-MM-DD
 * - Display: MM-DD-YYYY
 */

/**
 * Converts a date value to YYYY-MM-DD format for API requests
 * @param {Date|string|object} dateValue - Date to convert
 * @returns {string|null} - Date in YYYY-MM-DD format or null
 */
export const formatDateForAPI = (dateValue) => {
  if (!dateValue) return null;

  try {
    let date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      // Accept MM-DD-YYYY or YYYY-MM-DD strings
      if (dateValue.length === 10 && dateValue.includes('-')) {
        const [a, b, c] = dateValue.split('-');
        if (a.length === 4) {
          // already YYYY-MM-DD
          return `${a}-${b}-${c}`;
        }
        // assume MM-DD-YYYY → normalize below
        date = new Date(Number(c), Number(a) - 1, Number(b));
      } else {
        date = new Date(dateValue);
      }
      if (isNaN(date)) return null;
    } else if (typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
      // Handle moment/dayjs objects
      date = dateValue.toDate();
    } else {
      return null;
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${year}-${month}-${day}`;
  } catch (e) {
    return null;
  }
};

/**
 * Converts a date value to MM-DD-YYYY format for display
 * @param {Date|string|object} dateValue - Date to convert
 * @returns {string} - Date in MM-DD-YYYY format or '-'
 */
export const formatDateForDisplay = (dateValue) => {
  if (!dateValue) return '-';

  try {
    let date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      let dateString = dateValue;
      if (dateString.includes('T')) {
        dateString = dateString.split('T')[0];
      }

      // Handle both YYYY-MM-DD (API) and MM-DD-YYYY inputs
      if (dateString.includes('-') && dateString.length === 10) {
        const parts = dateString.split('-');
        if (parts.length === 3) {
          if (parts[0].length === 4) {
            // YYYY-MM-DD
            const [year, month, day] = parts.map(Number);
            date = new Date(year, month - 1, day);
          } else {
            // MM-DD-YYYY
            const [month, day, year] = parts.map(Number);
            date = new Date(year, month - 1, day);
          }
        }
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date)) return '-';
    } else if (typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else {
      return '-';
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    return `${month}-${day}-${year}`;
  } catch (e) {
    return '-';
  }
};

/**
 * Converts a date value to MM-DD-YYYY HH:MM AM/PM format for display
 * @param {Date|string|object} dateValue - Date to convert
 * @returns {string} - Date and time in MM-DD-YYYY HH:MM AM/PM format or '-'
 */
export const formatDateTimeForDisplay = (dateValue) => {
  if (!dateValue) return '-';

  try {
    let date;

    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
      if (isNaN(date)) return '-';
    } else if (typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else {
      return '-';
    }

    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();

    // Format time in 12-hour format with AM/PM
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const timeString = `${hours}:${minutes} ${ampm}`;

    return `${month}-${day}-${year} ${timeString}`;
  } catch (e) {
    return '-';
  }
};

/**
 * Parses a date from API response (prefers YYYY-MM-DD, supports MM-DD-YYYY fallback)
 * @param {string} dateString - Date string from API
 * @returns {Date|null} - Parsed Date object or null
 */
export const parseDateFromAPI = (dateString) => {
  if (!dateString) return null;

  try {
    let dString = dateString;
    if (typeof dString === 'object' && dString instanceof Date) return dString;
    if (typeof dString === 'object' && typeof dString.toDate === 'function') return dString.toDate();

    if (typeof dString === 'string' && dString.includes('T')) {
      dString = dString.split('T')[0];
    }

    // Handle YYYY-MM-DD and MM-DD-YYYY for backwards compatibility
    if (typeof dString === 'string' && dString.includes('-') && dString.length === 10) {
      const parts = dString.split('-');
      if (parts.length === 3) {
        if (parts[0].length === 4) {
          // YYYY-MM-DD
          const [year, month, day] = parts.map(Number);
          return new Date(year, month - 1, day);
        } else {
          // MM-DD-YYYY
          const [month, day, year] = parts.map(Number);
          return new Date(year, month - 1, day);
        }
      }
    }

    // Fallback to standard Date parsing
    // Prevent parsing partial strings like "1", "12-", "01-01-200" which cause jumps
    if (typeof dString === 'string' && dString.length < 8) return null;

    const date = new Date(dString);
    if (isNaN(date)) return null;

    // Reject dates with years less than 1000 (e.g. typing "202" gives year 202)
    if (date.getFullYear() < 1000) return null;

    return date;
  } catch (e) {
    return null;
  }
};

/**
 * Formats date for datetime picker components (maintains Date object)
 * @param {string} dateString - Date string from API
 * @returns {Date|string} - Date object for picker or empty string
 */
export const formatDateForPicker = (dateString) => {
  if (!dateString) return '';

  const date = parseDateFromAPI(dateString);
  // Return original string if parsing uncompleted date, so picker doesn't lose user's typing
  return date || dateString;
};

/**
 * Gets current date in YYYY-MM-DD format for API
 * @returns {string} - Current date in YYYY-MM-DD format
 */
export const getCurrentDateForAPI = () => {
  return formatDateForAPI(new Date());
};

/**
 * Gets current date in MM-DD-YYYY format for display
 * @returns {string} - Current date in MM-DD-YYYY format
 */
export const getCurrentDateForDisplay = () => {
  return formatDateForDisplay(new Date());
};

/**
 * Validates if a date string is in MM-DD-YYYY format
 * @param {string} dateString - Date string to validate
 * @returns {boolean} - True if valid MM-DD-YYYY format
 */
export const isValidAmericanDateFormat = (dateString) => {
  if (!dateString || typeof dateString !== 'string') return false;

  const regex = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-\d{4}$/;
  if (!regex.test(dateString)) return false;

  const [month, day, year] = dateString.split('-').map(Number);
  const date = new Date(year, month - 1, day);

  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day;
};
